import React, { useMemo, memo } from 'react';
import { CALLS } from '../../../utils/search/constants';
import {
  ResultsContainer as StyledResultsContainer,
  OverflowWrapper,
} from './ConnectedUniversalSearch.styles';
import { ErrorWrapper } from './ErrorWrapper';
import StyledAxon from './styledDropdown/StyledAxon';
import StyledCall from './styledDropdown/StyledCall';
import StyledOutboundSMS from './styledDropdown/StyledOutboundSMS';
import StyledSuggestedAddress from './styledDropdown/StyledSuggestedAddress';

const SEARCH_INPUT_PADDING = 20;
const RESULT_CONTAINER_MAX_HEIGHT = '224px';

export const ResultsContainer = memo(
  ({
    displayResultContainer,
    hasError,
    isValidW3W,
    mouseCursor,
    isInternational,
    formatPhoneWithDashes,
    handleSearchResult,
    handleSearchOutboundSMS,
    isPremiumUser,
    hasGisSearchAccess,
    resultContainerRef,
    gisSearchResults = [],
    filteredCallResults = [],
    filteredAxonResults = [],
    filteredOutboundSMSResults = [],
    top,
    left,
    width,
    handleSelectedAddress = () => {},
    handleAxonSearchResult,
  }) => {
    const maxWidth =
      document.documentElement.clientWidth - SEARCH_INPUT_PADDING; // ugly solution for making fixed width elements dynamic while resizing the screen

    const combinedResults = useMemo(() => {
      return [
        ...filteredAxonResults,
        ...filteredCallResults,
        ...filteredOutboundSMSResults,
        ...gisSearchResults,
      ];
    }, [
      filteredAxonResults,
      filteredCallResults,
      filteredOutboundSMSResults,
      gisSearchResults,
    ]);

    const isVisible = useMemo(() => {
      return displayResultContainer && (hasError || combinedResults.length > 0);
    }, [combinedResults.length, displayResultContainer, hasError]);
    return isVisible ? (
      <StyledResultsContainer
        maxWidth={maxWidth}
        hasError={hasError}
        top={top}
        left={left}
        width={width}
      >
        {hasError && combinedResults.length === 0 ? (
          <ErrorWrapper isValidW3W={isValidW3W} />
        ) : (
          <OverflowWrapper
            maxHeight={RESULT_CONTAINER_MAX_HEIGHT}
            ref={resultContainerRef}
          >
            {combinedResults.reduce((accumulator, result, idx) => {
              if (result.data?.source === 'axon') {
                const styledData = [
                  ...accumulator,
                  <StyledAxon
                    key={idx}
                    axon={result.data}
                    index={idx}
                    mouseCursor={mouseCursor}
                    maxWidth={maxWidth}
                    handleAxonSearchResult={handleAxonSearchResult}
                  />,
                ];
                return styledData;
              } else if (
                result.data?._datasetName === CALLS &&
                (result.data?.core?.locations?.length ||
                  result.data?.core?.secondaryLocations?.length)
              ) {
                const styledData = [
                  ...accumulator,
                  <StyledCall
                    key={idx}
                    call={result.data}
                    index={idx}
                    mouseCursor={mouseCursor}
                    isPremiumUser={isPremiumUser}
                    formatPhoneWithDashes={formatPhoneWithDashes}
                    handleSearchResult={handleSearchResult}
                    isInternational={isInternational}
                    maxWidth={maxWidth}
                  />,
                ];
                return styledData;
              } else if (result.data?.callerID) {
                const styledData = [
                  ...accumulator,
                  <StyledOutboundSMS
                    key={idx}
                    call={result.data}
                    index={idx}
                    mouseCursor={mouseCursor}
                    formatPhoneWithDashes={formatPhoneWithDashes}
                    handleSearchOutboundSMS={handleSearchOutboundSMS}
                    isInternational={isInternational}
                    maxWidth={maxWidth}
                  />,
                ];
                return styledData;
              } else if (result.text && result.magicKey && hasGisSearchAccess) {
                // Suggestion from /suggest endpoint with text and magicKey
                const styledData = [
                  ...accumulator,
                  <StyledSuggestedAddress
                    key={idx}
                    suggestion={result}
                    index={idx}
                    mouseCursor={mouseCursor}
                    handleSelectedAddress={handleSelectedAddress}
                  />,
                ];
                return styledData;
              } else {
                return accumulator;
              }
            }, [])}
          </OverflowWrapper>
        )}
      </StyledResultsContainer>
    ) : null;
  },
);
